import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';

import {
  Bug,
  X,
  Check,
  XCircle,
  RotateCcw,
  Cookie,
  Trash2,
  Database,
  Eye,
  EyeOff,
  Settings,
  User,
  Shield,
  Globe,
  Activity,
  Wifi,
  WifiOff,
  Mail,
  Send,
} from 'lucide-react';


import { useSession } from '@/shared/hooks/useSession';
import { useTenant } from '@/shared/hooks/useTenant';
import { useAuth } from '@/shared/hooks/useAuth';
import { UserRole } from '@/shared/types';
import { getRoleDisplayName } from '@/shared/utils/role-utils';
import {
  APP_CONFIG,
  isDevelopment,
  isTesting,
  isProduction,
} from '@/shared/config/config';
import { sessionService } from '@/shared/services/session.service';
import { websocketService } from '@/shared/services/websocket.service';
import { privateApi } from '@/config/axios.config';
import { useGlobalNotifications } from '@/shared/contexts/NotificationContext';
import {
  clearAllStorage,
  clearSessionCookies,
} from '@/shared/utils/clearStorage';

const UnifiedDebugPanelContent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'auth' | 'session' | 'env' | 'redux' | 'websocket' | 'email'
  >('auth');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);

  const location = useLocation();
  const session = useSession();
  const tenant = useTenant();
  const { user } = useAuth();
  const { showSuccess, showError } = useGlobalNotifications();

  // Redux states
  const authState = useSelector((state: any) => state.auth);
  const adminState = useSelector((state: any) => state.admin);
  const notificationsState = useSelector((state: any) => state.notifications);

  const [emailStatus, setEmailStatus] = useState<{
    configured: boolean;
    provider: string;
    fromEmail: string;
    fromName: string;
    apiVersion: string;
    apiKeyPreview: string | null;
    debugInfo?: {
      configService: {
        hasApiKey: boolean;
        apiKeyLength: number;
        apiKeyPreview: string | null;
        fromEmail: string;
        fromName: string;
      };
      processEnv: {
        hasApiKey: boolean;
        apiKeyLength: number;
        apiKeyPreview: string | null;
        fromEmail: string | null;
        fromName: string | null;
      };
    };
  } | null>(null);
  const [isLoadingEmailStatus, setIsLoadingEmailStatus] = useState(false);

  const debugInfo = sessionService.getDebugInfo();
  const sessionDebugInfo = session.getSessionDebugInfo();

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  const clearCookies = () => {
    clearSessionCookies();
  };

  const clearAll = () => {
    clearAllStorage();
  };

  const reload = () => {
    window.location.reload();
  };

  // Cargar estado de email desde el backend
  useEffect(() => {
    const loadEmailStatus = async () => {
      if (activeTab === 'email' && session.isAuthenticated) {
        setIsLoadingEmailStatus(true);
        
        try {
          const response = await privateApi.get('/email/status');
          
          // El interceptor envuelve la respuesta en { success: true, data: {...} }
          const statusData = response.data?.data || response.data;
          
          if (statusData && typeof statusData === 'object') {
            setEmailStatus(statusData);
          } else {
            throw new Error('Formato de respuesta inválido');
          }
        } catch (error: any) {
          // Establecer estado por defecto en caso de error
          const defaultStatus = {
            configured: false,
            provider: 'SendGrid',
            fromEmail: 'noreply@inbomarket.com',
            fromName: 'InboMarket',
            apiVersion: 'v3',
            apiKeyPreview: null,
          };
          setEmailStatus(defaultStatus);
        } finally {
          setIsLoadingEmailStatus(false);
        }
      } else if (activeTab === 'email' && !session.isAuthenticated) {
        // Si no está autenticado, establecer estado por defecto
        setEmailStatus({
          configured: false,
          provider: 'SendGrid',
          fromEmail: 'noreply@inbomarket.com',
          fromName: 'InboMarket',
          apiVersion: 'v3',
          apiKeyPreview: null,
        });
      }
    };

    loadEmailStatus();
  }, [activeTab, session.isAuthenticated]);

  const handleSendTestEmail = async () => {
    const userEmail = user?.email || session.user?.email || authState?.user?.email;
    
    if (!userEmail) {
      showError('Error', 'No se pudo obtener tu correo electrónico. Asegúrate de estar autenticado.');
      return;
    }

    setIsSendingTestEmail(true);
    
    try {
      const response = await privateApi.post('/email/test');
      
      if (response.data?.success) {
        showSuccess(
          'Email de Prueba Enviado',
          `Se ha enviado un email de prueba a ${userEmail}. Revisa tu bandeja de entrada.`
        );
      } else {
        throw new Error(response.data?.errors?.[0] || 'Error al enviar el email');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0] || 
                          error.message || 
                          'Error al enviar el email de prueba';
      showError('Error al Enviar Email', errorMessage);
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const StatusIcon: React.FC<{ status: boolean }> = ({ status }) => {
    return status ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  };

  const getRoleColor = (role: UserRole) => {
    const colors: Record<UserRole, string> & { REAL_ESTATE_AGENT_ADMIN: string } = {
      [UserRole.SUPER_ADMIN]: 'bg-red-100 text-red-800',
      [UserRole.AGENCY_ADMIN]: 'bg-blue-100 text-blue-800',
      [UserRole.BROKER_MANAGER_ADMIN]: 'bg-emerald-100 text-emerald-800',
      [UserRole.PROJECT_DEVELOPER_ADMIN]: 'bg-green-100 text-green-800',
      [UserRole.AGENCY_AGENT_USER]: 'bg-purple-100 text-purple-800',
      [UserRole.ACCOUNTING_TEAM]: 'bg-yellow-100 text-yellow-800',
      [UserRole.OPERATIONAL_TEAM]: 'bg-orange-100 text-orange-800',
      [UserRole.CONDOMINIUM_ADMIN]: 'bg-cyan-100 text-cyan-800',
      [UserRole.LEGAL_MODULE]: 'bg-indigo-100 text-indigo-800',
      [UserRole.BUYER_CLIENT]: 'bg-gray-100 text-gray-800',
      [UserRole.PENDING_ONBOARDING]: 'bg-amber-100 text-amber-800',
      REAL_ESTATE_AGENT_ADMIN: 'bg-purple-100 text-purple-800', // @deprecated, mismo color que agente
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-ES');
  };

  const maskToken = (token: string | undefined) => {
    if (!token) return 'N/A';
    if (!showTokens) return `${token.substring(0, 10)}...`;
    return token;
  };

  const getEnvironmentColor = () => {
    if (isDevelopment()) return 'bg-green-100 text-green-800';
    if (isTesting()) return 'bg-yellow-100 text-yellow-800';
    if (isProduction()) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-50">
        <Button
          onClick={togglePanel}
          size="sm"
          variant="outline"
          className="rounded-full shadow-lg bg-white hover:bg-gray-50 flex items-center justify-center"
          title="Debug Panel"
        >
          <Bug className="h-4 w-4 text-gray-700 flex-shrink-0" />
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'auth', label: 'Auth', icon: Shield },
    { id: 'session', label: 'Session', icon: User },
    { id: 'env', label: 'Environment', icon: Globe },
    { id: 'redux', label: 'Redux', icon: Database },
    { id: 'websocket', label: 'WebSocket', icon: Wifi },
    { id: 'email', label: 'Email', icon: Mail },
  ] as const;

  return (
    <div className="fixed bottom-4 left-4 z-50 w-96 max-h-[80vh] overflow-y-auto">
      <Card className="shadow-xl border-2 bg-white">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Bug className="h-5 w-5 text-green-500" />
              <CardTitle className="text-lg">Debug Panel</CardTitle>
            </div>
            <div className="flex items-center space-x-1">
              <Button
                onClick={() => setShowTokens(!showTokens)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                title={showTokens ? 'Hide tokens' : 'Show tokens'}
              >
                {showTokens ? (
                  <EyeOff className="h-3 w-3" />
                ) : (
                  <Eye className="h-3 w-3" />
                )}
              </Button>
              <Button
                onClick={togglePanel}
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-3">
            <div className="flex space-x-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <Button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    variant={activeTab === tab.id ? 'primary' : 'ghost'}
                    size="sm"
                    className="flex-shrink-0 text-xs h-7 min-w-fit whitespace-nowrap"
                  >
                    <Icon className="h-3 w-3 mr-1" />
                    {tab.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 text-sm">
          {/* Auth Tab */}
          {activeTab === 'auth' && (
            <div className="space-y-4">
              {/* Estado de Autenticación */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Shield className="h-4 w-4 mr-1" />
                  Authentication State
                </h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Authenticated:</span>
                    <StatusIcon status={session.isAuthenticated} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Loading:</span>
                    <span
                      className={
                        session.loading ? 'text-yellow-600' : 'text-gray-600'
                      }
                    >
                      {session.loading ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {session.error && (
                    <div className="flex justify-between">
                      <span>Error:</span>
                      <span className="text-red-600 text-xs">
                        {session.error}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Información del Usuario */}
              {session.user && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    User Information
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>ID:</span>
                      <span className="font-mono text-xs">
                        {session.user.id}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Email:</span>
                      <span className="text-xs">{session.user.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Name:</span>
                      <span className="text-xs">
                        {session.user.firstName} {session.user.lastName}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Role:</span>
                      <Badge className={getRoleColor(session.user.role)}>
                        {getRoleDisplayName(session.user.role)}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Tenant ID:</span>
                      <span className="font-mono text-xs">
                        {session.user.tenantId}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Last Login:</span>
                      <span className="text-xs">
                        {formatDate(session.user.lastLogin)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Active:</span>
                      <StatusIcon status={session.user.isActive} />
                    </div>
                  </div>
                </div>
              )}

              {/* Verificaciones de Rol */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Role Checks
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Is Admin:</span>
                    <StatusIcon status={session.isAdmin()} />
                  </div>
                  <div className="flex justify-between">
                    <span>Is Business Admin:</span>
                    <StatusIcon status={session.isBusinessAdmin()} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Session Tab */}
          {activeTab === 'session' && (
            <div className="space-y-4">
              {/* Session Service Info */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Activity className="h-4 w-4 mr-1" />
                  Session Service
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Has Token:</span>
                    <StatusIcon status={debugInfo.hasToken} />
                  </div>
                  <div className="flex justify-between">
                    <span>Has User:</span>
                    <StatusIcon status={debugInfo.hasUser} />
                  </div>
                  <div className="flex justify-between">
                    <span>User Role:</span>
                    <span className="text-xs">
                      {debugInfo.userRole || 'None'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Active Session:</span>
                    <StatusIcon status={session.hasActiveSession()} />
                  </div>
                </div>
              </div>

              {/* Tokens */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Tokens</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Access Token:</span>
                    <span className="font-mono text-xs break-all">
                      {maskToken(session.token || undefined)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Refresh Token:</span>
                    <span className="font-mono text-xs break-all">
                      {maskToken(session.refreshToken || undefined)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Tenant Info */}
              {tenant.currentTenant && (
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Tenant</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Name:</span>
                      <span className="text-xs">
                        {tenant.currentTenant.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Subdomain:</span>
                      <span className="text-xs">
                        {tenant.currentTenant.subdomain}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Path */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Current Path
                </h4>
                <div className="text-xs font-mono bg-gray-100 p-2 rounded">
                  {location.pathname}
                </div>
              </div>
            </div>
          )}

          {/* Environment Tab */}
          {activeTab === 'env' && (
            <div className="space-y-4">
              {/* Environment Info */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Globe className="h-4 w-4 mr-1" />
                  Environment
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span>Environment:</span>
                    <Badge className={getEnvironmentColor()}>
                      {APP_CONFIG.environment.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>API URL:</span>
                    <span className="text-xs font-mono">
                      {APP_CONFIG.api.baseUrl}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Version:</span>
                    <span className="text-xs">{APP_CONFIG.version}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>App Name:</span>
                    <span className="text-xs">{APP_CONFIG.name}</span>
                  </div>
                </div>
              </div>

              {/* System Info */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Settings className="h-4 w-4 mr-1" />
                  System Info
                </h4>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>User Agent: {navigator.userAgent.split(' ')[0]}...</div>
                  <div>Timestamp: {new Date().toLocaleTimeString()}</div>
                  <div>URL: {window.location.origin}</div>
                  <div>Mode: {import.meta.env.MODE}</div>
                </div>
              </div>
            </div>
          )}

          {/* Redux Tab */}
          {activeTab === 'redux' && (
            <div className="space-y-4">
              {/* Auth State */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Auth State (Redux)
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Loading:</span>
                    <StatusIcon status={authState?.loading} />
                  </div>
                  <div className="flex justify-between">
                    <span>Authenticated:</span>
                    <StatusIcon status={authState?.isAuthenticated} />
                  </div>
                  <div className="flex justify-between">
                    <span>User:</span>
                    <span className="text-xs">
                      {authState?.user?.email || 'None'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Token:</span>
                    <StatusIcon status={!!authState?.token} />
                  </div>
                </div>
              </div>

              {/* Admin State */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Admin State (Redux)
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Loading:</span>
                    <StatusIcon status={adminState?.loading} />
                  </div>
                  <div className="flex justify-between">
                    <span>Authenticated:</span>
                    <StatusIcon status={adminState?.isAuthenticated} />
                  </div>
                  <div className="flex justify-between">
                    <span>User:</span>
                    <span className="text-xs">
                      {adminState?.user?.email || 'None'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Role:</span>
                    <span className="text-xs">
                      {adminState?.role || 'None'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Redux Debug */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">
                  Redux Debug
                </h4>
                <div className="text-xs bg-gray-100 p-2 rounded font-mono">
                  {JSON.stringify(sessionDebugInfo.reduxState, null, 2)}
                </div>
              </div>
            </div>
          )}

          {/* WebSocket Tab */}
          {activeTab === 'websocket' && (
            <div className="space-y-4">
              {/* Estado de Conexión */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Wifi className="h-4 w-4 mr-1" />
                  Connection Status
                </h4>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Connected:</span>
                    <div className="flex items-center">
                      {websocketService.getIsConnected() ? (
                        <Wifi className="h-4 w-4 text-green-500 mr-1" />
                      ) : (
                        <WifiOff className="h-4 w-4 text-red-500 mr-1" />
                      )}
                      <span className={websocketService.getIsConnected() ? 'text-green-600' : 'text-red-600'}>
                        {websocketService.getIsConnected() ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span>Connecting:</span>
                    <span className={notificationsState?.wsConnecting ? 'text-yellow-600' : 'text-gray-600'}>
                      {notificationsState?.wsConnecting ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {notificationsState?.wsError && (
                    <div className="flex justify-between">
                      <span>Error:</span>
                      <span className="text-red-600 text-xs">
                        {notificationsState.wsError}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Información de Conexión */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Activity className="h-4 w-4 mr-1" />
                  Connection Info
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Socket ID:</span>
                    <span className="font-mono text-xs">
                      {websocketService.getSocketId() || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Server Connected:</span>
                    <StatusIcon status={websocketService.isConnectedToServer()} />
                  </div>
                </div>
              </div>

              {/* Estadísticas de Conexión */}
              {(() => {
                const stats = websocketService.getConnectionStats();
                return (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <Settings className="h-4 w-4 mr-1" />
                      Connection Stats
                    </h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Reconnect Attempts:</span>
                        <span className="text-xs">{stats.reconnectAttempts}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Last Activity:</span>
                        <span className="text-xs">
                          {stats.lastActivity.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Time Since Activity:</span>
                        <span className="text-xs">
                          {Math.round(stats.timeSinceLastActivity / 1000)}s
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Manually Disconnected:</span>
                        <StatusIcon status={stats.isManuallyDisconnected} />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Estado de Notificaciones */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Database className="h-4 w-4 mr-1" />
                  Notifications State
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span>Total Notifications:</span>
                    <span className="text-xs">{notificationsState?.notifications?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Unread Count:</span>
                    <span className="text-xs">{notificationsState?.stats?.unread || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Last Fetch:</span>
                    <span className="text-xs">
                      {notificationsState?.lastFetchTime 
                        ? new Date(notificationsState.lastFetchTime).toLocaleTimeString()
                        : 'Never'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Eventos Recientes */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                  <Activity className="h-4 w-4 mr-1" />
                  Recent Events
                </h4>
                <div className="text-xs bg-gray-100 p-2 rounded font-mono max-h-32 overflow-y-auto">
                  {notificationsState?.notifications?.length > 0 ? (
                    notificationsState.notifications.slice(0, 5).map((notification: any, index: number) => (
                      <div key={index} className="mb-1">
                        <span className="text-gray-500">[{new Date(notification.createdAt).toLocaleTimeString()}]</span>{' '}
                        <span className="text-blue-600">{notification.type}</span>
                        <div className="pl-4 text-gray-600 truncate">
                          {notification.message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500">No recent events</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              {isLoadingEmailStatus ? (
                <div className="flex items-center justify-center py-4">
                  <Activity className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-600">Cargando estado...</span>
                </div>
              ) : emailStatus ? (
                <>
                  {/* SendGrid Configuration */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-1" />
                      SendGrid Configuration
                    </h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>API Key:</span>
                        <div className="flex items-center">
                          <StatusIcon status={emailStatus.configured} />
                          <span className="ml-2 font-mono text-xs">
                            {emailStatus.apiKeyPreview || 'No configurado'}
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span>From Email:</span>
                        <span className="text-xs">{emailStatus.fromEmail}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>From Name:</span>
                        <span className="text-xs">{emailStatus.fromName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Service:</span>
                        <Badge className="bg-teal-100 text-teal-800">{emailStatus.provider}</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Email Service Status */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <Activity className="h-4 w-4 mr-1" />
                      Service Status
                    </h4>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Configured:</span>
                        <StatusIcon status={emailStatus.configured} />
                      </div>
                      <div className="flex justify-between">
                        <span>User Email:</span>
                        <span className="text-xs">
                          {user?.email || session.user?.email || authState?.user?.email || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Can Send Test:</span>
                        <StatusIcon status={!!(emailStatus.configured && user?.email)} />
                      </div>
                    </div>
                  </div>

                  {/* Test Email */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <Send className="h-4 w-4 mr-1" />
                      Test Email
                    </h4>
                    <div className="space-y-2">
                      <p className="text-xs text-gray-600">
                        Envía un email de prueba a tu dirección de correo para verificar la integración con SendGrid.
                      </p>
                      <Button
                        onClick={handleSendTestEmail}
                        disabled={
                          isSendingTestEmail || 
                          !emailStatus.configured || 
                          !(user?.email || session.user?.email || authState?.user?.email)
                        }
                        variant="primary"
                        size="sm"
                        className="w-full"
                      >
                        {isSendingTestEmail ? (
                          <>
                            <Activity className="h-4 w-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Enviar Email de Prueba
                          </>
                        )}
                      </Button>
                      {!emailStatus.configured && (
                        <p className="text-xs text-yellow-600">
                          ⚠️ API Key de SendGrid no configurada. Reinicia el servidor backend después de agregar la API key al archivo .env
                        </p>
                      )}
                      {!(user?.email || session.user?.email || authState?.user?.email) && (
                        <p className="text-xs text-yellow-600">
                          ⚠️ No se pudo obtener el email del usuario. Asegúrate de estar autenticado.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email Information */}
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2 flex items-center">
                      <Settings className="h-4 w-4 mr-1" />
                      Email Information
                    </h4>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Provider:</span>
                        <span className="font-semibold">{emailStatus.provider}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>API Version:</span>
                        <span>{emailStatus.apiVersion}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Endpoint:</span>
                        <span className="font-mono text-xs">/email/test</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Status:</span>
                        <Badge className={emailStatus.configured ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {emailStatus.configured ? 'Ready' : 'Not Configured'}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Debug Info */}
                  {emailStatus.debugInfo && (
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs space-y-2">
                      <h4 className="font-semibold text-gray-700 flex items-center">
                        <Bug className="h-4 w-4 mr-1" />
                        Debug Info (solo desarrollo)
                      </h4>
                      <div>
                        <p className="font-semibold text-gray-600 mb-1">ConfigService</p>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>Has API Key:</span>
                            <StatusIcon status={emailStatus.debugInfo.configService.hasApiKey} />
                          </div>
                          <div className="flex justify-between">
                            <span>API Key Preview:</span>
                            <span className="font-mono">{emailStatus.debugInfo.configService.apiKeyPreview || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>From Email:</span>
                            <span>{emailStatus.debugInfo.configService.fromEmail}</span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <p className="font-semibold text-gray-600 mb-1">process.env</p>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span>Has API Key:</span>
                            <StatusIcon status={emailStatus.debugInfo.processEnv.hasApiKey} />
                          </div>
                          <div className="flex justify-between">
                            <span>API Key Preview:</span>
                            <span className="font-mono">{emailStatus.debugInfo.processEnv.apiKeyPreview || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>From Email:</span>
                            <span>{emailStatus.debugInfo.processEnv.fromEmail || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-[8px] text-gray-500 mt-1">
                        Usa esta información para verificar si el backend está leyendo las variables de entorno. Solo visible en desarrollo.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <div className="text-center py-4 text-sm text-gray-500">
                    {isLoadingEmailStatus ? (
                      <div className="flex items-center justify-center">
                        <Activity className="h-4 w-4 animate-spin mr-2" />
                        Cargando estado...
                      </div>
                    ) : !session.isAuthenticated ? (
                      <div className="text-yellow-600">
                        ⚠️ Debes estar autenticado para ver el estado de email
                      </div>
                    ) : (
                      <div>
                        <p className="text-red-600 mb-2">No se pudo cargar el estado de email</p>
                        <p className="text-xs text-gray-500">
                          Verifica la consola del navegador para más detalles
                        </p>
                      </div>
                    )}
                  </div>
                  {!session.isAuthenticated && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                      El endpoint /email/status requiere autenticación. Por favor, inicia sesión primero.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-4 border-t">
            <div className="flex space-x-2">
              <Button
                onClick={clearCookies}
                variant="danger"
                size="sm"
                className="flex-1"
              >
                <Cookie className="h-4 w-4 mr-1" />
                Clear Cookies
              </Button>
              <Button
                onClick={clearAll}
                variant="danger"
                size="sm"
                className="flex-1"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear All
              </Button>
              <Button
                onClick={reload}
                variant="primary"
                size="sm"
                className="flex-1"
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Reload
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const UnifiedDebugPanel: React.FC = () => {
  // Solo mostrar en desarrollo
  if (!isDevelopment()) {
    return null;
  }

  return <UnifiedDebugPanelContent />;
};

export default UnifiedDebugPanel;
